import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Reveal, Kicker } from "./Reveal";
import { BookOpen, ListChecks, FileText, ArrowRight } from "lucide-react";

export default function ResourcesPreview() {
  const [articles, setArticles] = useState([]);
  const [quizzes, setQuizzes] = useState([]);

  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data.slice(0, 3))).catch(() => {});
    api.get("/quizzes").then((r) => setQuizzes(r.data.slice(0, 2))).catch(() => {});
  }, []);

  return (
    <section id="ressources" className="py-16 md:py-24 lg:py-32" data-testid="resources-preview-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Kicker>Ressources</Kicker>
          <div className="mt-5 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-50 max-w-xl">
              Articles, quiz et outils — un espace qui grandit avec vous.
            </h2>
            <Link
              to="/agilite"
              data-testid="resources-see-all-link"
              className="inline-flex items-center gap-2 text-sm text-teal-300 hover:text-teal-200 transition-colors"
            >
              Tous les articles <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {articles.map((a, i) => (
            <Reveal key={a.article_id} delay={i * 0.08}>
              <Link
                to={`/agilite/${a.slug}`}
                data-testid={`preview-article-${a.slug}`}
                className="block border border-white/10 bg-[#131b2c] card-lift h-full overflow-hidden"
              >
                {a.image_url && (
                  <img src={a.image_url} alt={a.title} className="w-full h-40 object-cover opacity-70" />
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-[11px] font-mono2 uppercase tracking-[0.2em] text-blue-400">
                    <BookOpen size={12} /> {a.category} · {a.read_time}
                  </div>
                  <h3 className="mt-3 font-heading text-lg font-semibold text-slate-100 leading-snug">{a.title}</h3>
                  <p className="mt-2 text-sm text-slate-500 line-clamp-2">{a.excerpt}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <div className="mt-5 grid md:grid-cols-3 gap-5">
          {quizzes.map((q, i) => (
            <Reveal key={q.quiz_id} delay={i * 0.08}>
              <Link
                to={`/quizz/${q.quiz_id}`}
                data-testid={`preview-quiz-${q.quiz_id}`}
                className="block border border-teal-400/20 bg-[#10201f] card-lift p-6 h-full"
              >
                <div className="flex items-center gap-2 text-[11px] font-mono2 uppercase tracking-[0.2em] text-teal-300">
                  <ListChecks size={12} /> Quiz · {q.question_count} questions · {q.duration}
                </div>
                <h3 className="mt-3 font-heading text-lg font-semibold text-slate-100 leading-snug">{q.title}</h3>
                <p className="mt-2 text-sm text-slate-500 line-clamp-2">{q.description}</p>
              </Link>
            </Reveal>
          ))}
          <Reveal delay={0.16}>
            <div className="border border-dashed border-amber-400/30 bg-[#1a1610] p-6 h-full" data-testid="preview-whitepaper-teaser">
              <div className="flex items-center gap-2 text-[11px] font-mono2 uppercase tracking-[0.2em] text-amber-400">
                <FileText size={12} /> Bientôt disponible
              </div>
              <h3 className="mt-3 font-heading text-lg font-semibold text-slate-100">Livre blanc & FlashDiag Agilité</h3>
              <p className="mt-2 text-sm text-slate-500">
                Notre livre blanc est en cours de rédaction et le FlashDiag arrive bientôt.
              </p>
              <div className="mt-4 flex gap-4">
                <button
                  onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                  className="text-sm text-amber-300 hover:text-amber-200 transition-colors"
                  data-testid="preview-whitepaper-cta"
                >
                  Être averti →
                </button>
                <Link to="/flashdiag" className="text-sm text-blue-400 hover:text-blue-300 transition-colors" data-testid="preview-flashdiag-link">
                  Découvrir le FlashDiag →
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
